import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import DraggableGrid, { DraggableItem } from 'ruuri'
import { debounce } from 'lodash'
import { ChartOptions, ChartSize, ChartSizes } from 'src/types/chartTypes'
import { triggerChartRedraw } from 'src/utility'
import { Chart } from '../Chart'
import { useStableChartIds } from './trend-layout.utils'

interface TrendChartListProps {
  charts: ChartOptions[]
  setChartOrder: (newOrder: number[]) => void
  selectedSize: ChartSize
  isFullWidth: boolean
}

function Layout({
  charts,
  setChartOrder,
  selectedSize,
  isFullWidth,
}: TrendChartListProps): JSX.Element {
  const chartIds = useStableChartIds(charts)
  const ref = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  const checkContainerSize = useCallback(() => {
    if (!ref.current) return
    // A problem here is that clientWidth and clientHeight are rounded to whole numbers
    // For example a container width of 1025.5 gets rounded up to 1026.
    // This causes an issue when calculating boxSize. For example, with 3 columns and a
    // clientWidth of 1026 each box has a width of 342, but these don't fit into the
    // actual width of 1025.5.
    // Ideally we'd take the floor of the actual values, but this isn't available so we just take 1
    const width = ref.current.clientWidth - 1
    const height = ref.current.clientHeight - 1
    // only update if there are changes
    if (width !== containerSize.width || height !== containerSize.height) {
      setContainerSize({ width, height })
    }
  }, [containerSize, setContainerSize])

  // set the initial size after layout complete
  useLayoutEffect(() => {
    checkContainerSize()
  }, [checkContainerSize])

  // resize handler
  useEffect(() => {
    const debouncedHandleResize = debounce(() => {
      checkContainerSize()
    }, 100)
    window.addEventListener('resize', debouncedHandleResize)
    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
    }
  }, [checkContainerSize])

  const boxSize = useMemo(() => {
    // handle the single chart case
    if (charts.length === 1) {
      return { width: containerSize.width, height: containerSize.height }
    }

    const chartSize = ChartSizes[selectedSize]

    // if containerSize.width < chartSize.size we still want one column
    const columns = Math.floor(containerSize.width / chartSize.size) || 1
    const width = Math.floor(containerSize.width / columns)
    const height = Math.floor(width / 1.7)

    if (isFullWidth) {
      return { width: containerSize.width, height }
    }

    return { width, height }
  }, [charts.length, containerSize, isFullWidth, selectedSize])

  // trigger chart redraw when the boxSize changes
  useEffect(() => {
    triggerChartRedraw()
  }, [boxSize])

  const overflowSetting = charts.length > 1 ? 'overflow-hidden' : ''

  const draggableGrid = useMemo(() => {
    return (
      <DraggableGrid
        dragHandle={'.drag-handle'}
        dragEnabled={true}
        layoutDuration={500}
        onLayoutEnd={layout => {
          const newOrder = layout.map(item => {
            const id = item.getElement()?.children[0]?.id
            // if the id is missing (-1) or unparseable (NaN) it should be an error
            return id ? parseInt(id, 10) : -1
          })
          setChartOrder(newOrder)
        }}
      >
        {chartIds.map(id => (
          <DraggableItem
            itemID={id.toString()}
            id={id.toString()}
            key={id}
            className="!cursor-default"
          >
            <div
              className="p-2"
              style={{
                width: boxSize.width,
                maxWidth: containerSize.width,
                height: boxSize.height,
              }}
            >
              <Chart key={id} chartId={id} />
            </div>
          </DraggableItem>
        ))}
      </DraggableGrid>
    )
  }, [chartIds, setChartOrder, boxSize, containerSize])

  return (
    <div
      className={`mx-2 size-full max-h-full max-w-full overflow-y-auto ${overflowSetting}`}
      ref={ref}
    >
      {draggableGrid}
    </div>
  )
}

function shouldLayoutRerender(
  prevProps: TrendChartListProps,
  nextProps: TrendChartListProps,
): boolean {
  if (prevProps.selectedSize !== nextProps.selectedSize) return false
  if (prevProps.isFullWidth !== nextProps.isFullWidth) return false
  if (prevProps.charts.length !== nextProps.charts.length) return false

  // Only re-render if the order has changed
  return (
    prevProps.charts.map(ch => ch.id).toString() ===
    nextProps.charts.map(ch => ch.id).toString()
  )
}

export const TrendLayout = memo(Layout, shouldLayoutRerender)
